import React, { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Paper, Typography, ButtonGroup, Button, Card, CardContent, CircularProgress, Divider, List, ListItem, ListItemIcon, ListItemText, Chip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import TimelineIcon from '@mui/icons-material/Timeline';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RecommendIcon from '@mui/icons-material/Recommend';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import memberService from '../../services/memberService';

// Enhanced color palette with more vibrant colors
const COLORS = ['#3F51B5', '#00BCD4', '#FFC107', '#FF5722', '#9C27B0', '#4CAF50', '#E91E63', '#009688'];

const TimeframeSelector = ({ timeframe, onChange }) => (
  <ButtonGroup size="small" aria-label="timeframe selection">
    <Button variant={timeframe === '1m' ? 'contained' : 'outlined'} onClick={() => onChange('1m')}>1M</Button>
    <Button variant={timeframe === '3m' ? 'contained' : 'outlined'} onClick={() => onChange('3m')}>3M</Button>
    <Button variant={timeframe === '6m' ? 'contained' : 'outlined'} onClick={() => onChange('6m')}>6M</Button>
    <Button variant={timeframe === '1y' ? 'contained' : 'outlined'} onClick={() => onChange('1y')}>1Y</Button>
  </ButtonGroup>
);

const MetricCard = ({ title, value, subtitle, color }) => (
  <Card elevation={0} sx={{ 
    height: '100%', 
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
    transition: 'transform 0.3s, box-shadow 0.3s',
    borderLeft: color ? `4px solid ${color}` : 'none',
    backgroundColor: color ? `${color}10` : 'white',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)'
    }
  }}>
    <CardContent>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h5" component="div" sx={{ fontWeight: 'medium' }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// New component for insights and recommendations based on the data
const InsightsPanel = ({ growth = [], distribution = [], retention = 0, metrics = {} }) => {
  // Generate insights based on the data
  const insights = useMemo(() => {
    const result = [];
    
    // Growth trend analysis
    if (growth.length >= 2) {
      const lastMonth = growth[growth.length - 1];
      const previousMonth = growth[growth.length - 2];
      const growthRate = previousMonth.members > 0 
        ? ((lastMonth.members - previousMonth.members) / previousMonth.members) * 100 
        : 0;
      
      if (growthRate > 5) {
        result.push({
          type: 'positive',
          message: `Strong growth of ${growthRate.toFixed(1)}% in the last month`,
          icon: <TrendingUpIcon sx={{ color: '#4CAF50' }} />,
          priority: 1
        });
      } else if (growthRate < -5) {
        result.push({
          type: 'negative',
          message: `Membership declined by ${Math.abs(growthRate).toFixed(1)}% in the last month`,
          icon: <TrendingDownIcon sx={{ color: '#F44336' }} />,
          priority: 1
        });
      } else {
        result.push({
          type: 'neutral',
          message: `Stable membership with ${growthRate > 0 ? 'slight growth' : 'minimal change'} (${growthRate.toFixed(1)}%)`,
          icon: <TrendingFlatIcon sx={{ color: '#FF9800' }} />,
          priority: 2
        });
      }
      
      // Look for trends
      const recentTrend = analyzeTrend(growth.slice(-6));
      if (recentTrend === 'increasing') {
        result.push({
          type: 'positive',
          message: 'Positive growth trend over the last several months',
          icon: <TrendingUpIcon sx={{ color: '#4CAF50' }} />,
          priority: 2
        });
      } else if (recentTrend === 'decreasing') {
        result.push({
          type: 'warning',
          message: 'Declining trend in membership over recent months',
          icon: <WarningIcon sx={{ color: '#FF9800' }} />,
          priority: 1
        });
      }
    }
    
    // Retention analysis
    if (retention !== undefined) {
      if (retention < 70) {
        result.push({
          type: 'negative',
          message: `Low retention rate (${retention.toFixed(1)}%) indicates member engagement issues`,
          icon: <WarningIcon sx={{ color: '#F44336' }} />,
          priority: 1
        });
      } else if (retention > 90) {
        result.push({
          type: 'positive',
          message: `Excellent retention rate of ${retention.toFixed(1)}%`,
          icon: <CheckCircleIcon sx={{ color: '#4CAF50' }} />,
          priority: 2
        });
      }
    }
    
    // Distribution analysis
    if (distribution.length > 0) {
      const totalMembers = distribution.reduce((sum, item) => sum + item.count, 0);
      const largestSegment = distribution.reduce((max, item) => item.count > max.count ? item : max, { count: 0 });
      
      if (largestSegment.count > 0.6 * totalMembers) {
        result.push({
          type: 'warning',
          message: `${largestSegment.type} members represent ${((largestSegment.count / totalMembers) * 100).toFixed(0)}% of total membership - consider diversification`,
          icon: <DonutLargeIcon sx={{ color: '#FF9800' }} />,
          priority: 2
        });
      }
    }
    
    // Activity ratio analysis
    if (metrics.totalMembers && metrics.activeMembers) {
      const activityRatio = (metrics.activeMembers / metrics.totalMembers) * 100;
      if (activityRatio < 50) {
        result.push({
          type: 'negative',
          message: `Low active member ratio (${activityRatio.toFixed(0)}%) - engagement strategy needed`,
          icon: <WarningIcon sx={{ color: '#F44336' }} />,
          priority: 1
        });
      } else if (activityRatio > 80) {
        result.push({
          type: 'positive',
          message: `High activity ratio of ${activityRatio.toFixed(0)}% indicates strong engagement`,
          icon: <CheckCircleIcon sx={{ color: '#4CAF50' }} />,
          priority: 3
        });
      }
    }
    
    // Sort by priority
    return result.sort((a, b) => a.priority - b.priority);
  }, [growth, distribution, retention, metrics]);
  
  // Generate recommendations based on insights
  const recommendations = useMemo(() => {
    const recs = [];
    
    // Check churn rate
    if (metrics.churnRate > 5) {
      recs.push({
        message: 'Implement an exit survey to understand why members are leaving',
        priority: metrics.churnRate > 10 ? 1 : 2
      });
    }
    
    // Check retention rate
    if (retention < 80) {
      recs.push({
        message: 'Develop a member re-engagement campaign for inactive members',
        priority: 1
      });
    }
    
    // Check growth trend
    if (growth.length >= 3) {
      const lastThreeMonths = growth.slice(-3);
      const isDecreasing = lastThreeMonths[0].members > lastThreeMonths[2].members;
      
      if (isDecreasing) {
        recs.push({
          message: 'Consider a membership drive or promotional offer to boost growth',
          priority: 1
        });
      }
    }
    
    // Check member distribution
    if (distribution.length > 1) {
      const smallestSegment = distribution.reduce((min, item) => 
        item.count < min.count ? item : min, { count: Number.MAX_SAFE_INTEGER });
      
      const totalMembers = distribution.reduce((sum, item) => sum + item.count, 0);
      
      if (smallestSegment.count < 0.1 * totalMembers) {
        recs.push({
          message: `Focus on growing the ${smallestSegment.type} member segment through targeted outreach`,
          priority: 2
        });
      }
    }
    
    // Check active members ratio
    if (metrics.totalMembers && metrics.activeMembers) {
      const activityRatio = (metrics.activeMembers / metrics.totalMembers) * 100;
      if (activityRatio < 70) {
        recs.push({
          message: 'Create an engagement program to activate dormant members',
          priority: activityRatio < 50 ? 1 : 2
        });
      }
    }
    
    // Add general recommendations if we don't have enough
    if (recs.length < 2) {
      recs.push({
        message: 'Regularly survey members to understand their needs and satisfaction levels',
        priority: 3
      });
      
      recs.push({
        message: 'Implement a referral program to encourage existing members to refer new ones',
        priority: 3
      });
    }
    
    // Sort by priority
    return recs.sort((a, b) => a.priority - b.priority).slice(0, 3);
  }, [growth, distribution, retention, metrics]);
  
  return (
    <Grid container spacing={2}>
      {/* Insights Section */}
      <Grid item xs={12} md={6}>
        <Paper 
          sx={{ 
            p: { xs: 2, md: 3 }, 
            height: '100%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            borderRadius: 3,
            border: '1px solid rgba(0, 0, 0, 0.03)',
            transition: 'transform 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 6px 25px rgba(0, 0, 0, 0.1)'
            }
          }}
        >
          <Typography variant="subtitle1" sx={{ 
            mb: 2, 
            fontWeight: 600, 
            color: '#3f51b5',
            fontSize: '1.15rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            <LightbulbIcon sx={{ mr: 1.5, fontSize: '1.4rem' }} /> Key Insights
          </Typography>

          <List sx={{ p: 0 }}>
            {insights.length > 0 ? insights.map((insight, index) => (
              <ListItem 
                key={index} 
                alignItems="flex-start"
                sx={{ 
                  mb: 1.5, 
                  p: 1.5, 
                  bgcolor: insight.type === 'positive' ? 'rgba(76, 175, 80, 0.08)' : 
                           insight.type === 'negative' ? 'rgba(244, 67, 54, 0.08)' : 
                           insight.type === 'warning' ? 'rgba(255, 152, 0, 0.08)' : 
                           'rgba(33, 150, 243, 0.08)',
                  borderRadius: 1,
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {insight.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={insight.message}
                  sx={{ m: 0 }}
                />
                <Chip 
                  label={insight.type === 'positive' ? 'Positive' : 
                         insight.type === 'negative' ? 'Concern' : 
                         insight.type === 'warning' ? 'Warning' : 'Info'}
                  size="small"
                  sx={{ 
                    ml: 1,
                    bgcolor: insight.type === 'positive' ? 'rgba(76, 175, 80, 0.2)' : 
                             insight.type === 'negative' ? 'rgba(244, 67, 54, 0.2)' : 
                             insight.type === 'warning' ? 'rgba(255, 152, 0, 0.2)' : 
                             'rgba(33, 150, 243, 0.2)',
                    color: insight.type === 'positive' ? '#1b5e20' : 
                           insight.type === 'negative' ? '#b71c1c' : 
                           insight.type === 'warning' ? '#e65100' : 
                           '#0d47a1',
                  }}
                />
              </ListItem>
            )) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  Insufficient data to generate insights
                </Typography>
              </Box>
            )}
          </List>
        </Paper>
      </Grid>
      
      {/* Recommendations Section */}
      <Grid item xs={12} md={6}>
        <Paper 
          sx={{ 
            p: { xs: 2, md: 3 }, 
            height: '100%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            borderRadius: 3,
            border: '1px solid rgba(0, 0, 0, 0.03)',
            transition: 'transform 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 6px 25px rgba(0, 0, 0, 0.1)'
            }
          }}
        >
          <Typography variant="subtitle1" sx={{ 
            mb: 2, 
            fontWeight: 600, 
            color: '#FF5722',
            fontSize: '1.15rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            <RecommendIcon sx={{ mr: 1.5, fontSize: '1.4rem' }} /> Recommendations
          </Typography>

          <List sx={{ p: 0 }}>
            {recommendations.map((rec, index) => (
              <ListItem 
                key={index}
                alignItems="flex-start"
                sx={{ 
                  mb: 1.5, 
                  p: 1.5, 
                  bgcolor: 'rgba(255, 87, 34, 0.08)',
                  borderRadius: 1,
                  border: rec.priority === 1 ? '1px dashed rgba(255, 87, 34, 0.3)' : 'none'
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <RecommendIcon sx={{ color: rec.priority === 1 ? '#d84315' : '#ff5722' }} />
                </ListItemIcon>
                <ListItemText primary={rec.message} sx={{ m: 0 }} />
                {rec.priority === 1 && (
                  <Chip 
                    label="High Priority" 
                    size="small"
                    sx={{ 
                      ml: 1,
                      bgcolor: 'rgba(255, 87, 34, 0.2)',
                      color: '#d84315',
                    }}
                  />
                )}
              </ListItem>
            ))}
          </List>
        </Paper>
      </Grid>
    </Grid>
  );
};

// Helper function to analyze trends in data
const analyzeTrend = (dataPoints) => {
  if (!dataPoints || dataPoints.length < 3) return 'insufficient_data';
  
  let increases = 0;
  let decreases = 0;
  
  for (let i = 1; i < dataPoints.length; i++) {
    if (dataPoints[i].members > dataPoints[i-1].members) {
      increases++;
    } else if (dataPoints[i].members < dataPoints[i-1].members) {
      decreases++;
    }
  }
  
  const total = increases + decreases;
  if (total === 0) return 'stable';
  
  const increasePercent = (increases / total) * 100;
  
  if (increasePercent >= 70) return 'increasing';
  if (increasePercent <= 30) return 'decreasing';
  return 'fluctuating';
};

const MembershipAnalytics = ({ data, timeframe, onTimeframeChange }) => {
  const { growth = [], distribution = [], retention = 0, metrics = {} } = data || {};
  
  const formattedRetention = typeof retention === 'number' ? retention.toFixed(1) : '0.0';
  const churnRate = metrics.churnRate ? metrics.churnRate.toFixed(1) : '0.0';
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Membership Analytics</Typography>
        <TimeframeSelector timeframe={timeframe} onChange={onTimeframeChange} />
      </Box>
      
      <Grid container spacing={3}>
        {/* Membership Growth Chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Membership Growth</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={growth} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="members" 
                    stroke="#8884d8" 
                    activeDot={{ r: 8 }} 
                    name="Total Members"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Membership Distribution Chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Membership Distribution</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={distribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                    nameKey="type"
                  >
                    {distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Retention Rate */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1">Retention Rate</Typography>
              <Typography variant="h3" color="primary" sx={{ mt: 2 }}>
                {formattedRetention}%
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {timeframe === '1m' ? 'Monthly' : 
                 timeframe === '3m' ? 'Quarterly' : 
                 timeframe === '6m' ? 'Bi-annual' : 'Annual'} retention rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Churn Rate */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1">Churn Rate</Typography>
              <Typography variant="h3" color="error" sx={{ mt: 2 }}>
                {churnRate}%
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Members lost during this {timeframe === '1m' ? 'month' : 
                 timeframe === '3m' ? 'quarter' : 
                 timeframe === '6m' ? 'half-year' : 'year'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MembershipAnalytics;
